import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { Table } from '@fragment/ui/src/components/Table';

import { useGetDataExportsQuery } from '../../../../hooks/internalApi';
import { useInternalApiContext } from '../../../../hooks/useInternalApiContext';
import { getResultOrError } from '../../../../utils/errors';
import { useWorkspaceId } from '../../../../wrappers/WorkspaceProvider';
import { NewDataExportForm } from '../../../dataExport/NewDataExportForm';
import { DataExportDetail } from '../../../settings/DataExportDetail';
import { TableLoadError } from '../../../TableLoadError/TableLoadError';
import { SettingsModalLayout } from './SettingsModalLayout';
import { SettingsPageLoading } from './SettingsPageLoading';

export type DataExportRow = {
  id: string;
  exportBucketName: string;
  exportBucketRegion: string;
  exportBucketKey?: string | null;
};
const schema = [
  {
    title: 'Name',
    key: 'id',
  },
  {
    title: 'Bucket',
    key: 'exportBucketName',
    justify: 'right',
  },
] as const;

const emptyValue = {
  Bucket: 'N/A',
  Name: 'No Data Exports yet',
};
const DataExportsTable = ({
  onRowClick,
  onPendingChange,
}: {
  onRowClick: (row: DataExportRow) => void;
  onPendingChange: (isPending: boolean) => void;
}) => {
  const { context, token } = useInternalApiContext();
  const workspaceId = useWorkspaceId();
  const variables = useMemo(
    () => ({
      workspaceId,
    }),
    [workspaceId]
  );
  const [{ data, error, fetching }, reExecuteQuery] = useGetDataExportsQuery({
    variables,
    context,
    pause: !token,
  });
  const refreshTable = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );
  const { isError: isDataExportsError, result: dataExports } = getResultOrError(
    workspace?.dataExports,
    error
  );
  const isPending = !data && !error;
  useEffect(() => {
    onPendingChange(isPending);
  }, [isPending, onPendingChange]);
  if (isPending) return null;
  if (isWorkspaceError || isDataExportsError) {
    return (
      <TableLoadError
        entityColumnName="Name"
        rightColumnName="Bucket"
        {...{ error, retry: refreshTable, fetching }}
      />
    );
  }
  return (
    <Table
      data={dataExports.nodes}
      onRowClick={onRowClick}
      schema={schema}
      emptyValue={emptyValue}
      textSize="md"
      borderedRows
    />
  );
};

export const DataExportsSettingsForm = ({
  hideHeader = false,
}: { hideHeader?: boolean } = {}) => {
  const [clickedDataExport, setClickedDataExport] = useState<DataExportRow>();
  const [isCreatingDataExport, setIsCreatingDataExport] =
    useState<boolean>(false);
  const canCreateDataExport = useHasWorkspacePermission(
    Permissions.data_export.create
  );
  const [isListPending, setIsListPending] = useState(true);

  const handleBack = useCallback(() => {
    setClickedDataExport(undefined);
    setIsCreatingDataExport(false);
  }, []);

  const resolveViewState = (): 'list' | 'new' | 'detail' => {
    if (isCreatingDataExport) return 'new';
    if (clickedDataExport) return 'detail';
    return 'list';
  };
  const viewState = resolveViewState();

  return (
    <SettingsModalLayout
      hideHeader={hideHeader}
      title={match(viewState)
        .with('new', 'detail', () => 'Data Export details')
        .with('list', () => 'Data Exports')
        .exhaustive()}
      onBackButtonClick={viewState === 'list' ? undefined : handleBack}
      topButtonSection={
        viewState === 'list' &&
        !hideHeader &&
        canCreateDataExport &&
        !isListPending ? (
          <PrimaryButtonV2
            onClick={() => setIsCreatingDataExport(true)}
            secondaryIcon="+"
          >
            New Export
          </PrimaryButtonV2>
        ) : null
      }
    >
      {match(viewState)
        .with('new', () => (
          <NewDataExportForm onSuccess={handleBack} onCancel={handleBack} />
        ))
        .with('list', () => (
          <>
            {isListPending && <SettingsPageLoading />}
            <div className={isListPending ? 'hidden' : undefined}>
              <DataExportsTable
                onRowClick={setClickedDataExport}
                onPendingChange={setIsListPending}
              />
            </div>
          </>
        ))
        .with('detail', () => (
          <DataExportDetail
            onConfirmedDelete={handleBack}
            dataExport={clickedDataExport!}
          />
        ))
        .exhaustive()}
    </SettingsModalLayout>
  );
};
